import React from 'react';
import styled from 'styled-components';
import {
  Card,
  paletteHelper,
  Stack,
  Text,
  Strong,
  LandSizeMd,
  CarMd,
  spacingHelper,
} from '@construct-kit/core';
import Link from '../../../../shared/components/Link';
import BrandingBanner from '../../../detail/BrandingBanner';
import type { ElitePlusCarouselListing } from '../../model/elitePlusCarouselListingResponse';
import styles from './ElitePlusCarouselListingCard.scss';
import { clampAtLine } from './clampAtLine';
import { getPropertyTypeFromDisplayText } from '../../../../shared/model/constants/propertyTypes';
import trackingAdapter from '../../tracking/trackingAdapter';

const PlainLink = styled(Link)`
  display: inline-block;
  width: 240px;
`;

const StyledStack = styled(Stack)`
  overflow: hidden;
`;

const ImgBox = styled(Card)`
  overflow: hidden;
  box-shadow: none;
  border: 1px solid ${paletteHelper('borderSecondary')};
  border-radius: ${({ theme }) => theme.border.radius.large};
  margin-bottom: ${spacingHelper('twoExtraSmall')};
`;

const ListingImage = styled.img<{ src: string }>`
  background-size: cover;
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-image: url(${(props) => props.src});

  height: 164px;
`;

const Address = styled(Text)`
  color: ${paletteHelper('textPrimary')};
  margin-top: ${spacingHelper('twoExtraSmall')};
  margin-bottom: ${spacingHelper('small')};
  min-height: 48px;
  ${clampAtLine(2)};
`;

const Price = styled(Text)`
  color: ${paletteHelper('textSecondary')};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Channel = styled(Text)`
  margin-right: ${spacingHelper('small')};
`;

const Details = styled.div`
  display: flex;
  white-space: nowrap;
  flex-wrap: nowrap;
  color: ${paletteHelper('textSecondary')};
  margin-top: ${spacingHelper('twoExtraSmall')};
`;

const GeneralFeature = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${spacingHelper('small')};
`;

const FeatureText = styled(Text)`
  margin-left: ${spacingHelper('twoExtraSmall')};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const PropertyType = styled(Text)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export type ElitePlusCarouselListingCardProps = {
  listing: ElitePlusCarouselListing;
  bucket: string;
};

const transformChannel = (channel: string): string => {
  switch (channel) {
    case 'for-sale':
      return 'Buy';
    case 'for-lease':
      return 'Lease';
    default:
      return 'Buy';
  }
};

const showChannel = (channels: string[]): string => channels.map(transformChannel).join('/');

const trackElitePlusCarouselListingClick = (bucket: string) => {
  trackingAdapter.trackElitePlusCarouselListingClick(bucket);
};

const ElitePlusCarouselListingCard: React.FunctionComponent<ElitePlusCarouselListingCardProps> = ({
  listing,
  bucket,
}) => (
  <PlainLink
    href={listing.pdpUrl}
    aria-label="Elite+ Carousel Listing"
    onClick={() => trackElitePlusCarouselListingClick(bucket)}
  >
    <ImgBox>
      <BrandingBanner
        logo={listing.branding.logo}
        color={listing.branding.color}
        className={styles.bannerWrapper}
        logoClassName={styles.bannerLogo}
        containerClassName={styles.container}
      />
      <ListingImage src={listing.mainPhoto.url} />
    </ImgBox>
    <StyledStack gap="0" inset="0">
      <Address variant="subtitle01">
        <Strong>{`${listing.details.streetAddress}, ${listing.details.suburbAddress}`}</Strong>
      </Address>
      <Price variant="body02">{listing.details.price}</Price>
      <Details>
        <Channel variant="body02">{showChannel(listing.channels)}</Channel>
        <GeneralFeature>
          <LandSizeMd data-testid="elite-plus-listing-floorplan-logo" />
          <FeatureText variant="body02">{`${listing.attributes.area}`}</FeatureText>
        </GeneralFeature>
        {listing.attributes.carSpaces && (
          <GeneralFeature>
            <CarMd data-testid="elite-plus-listing-car-space-logo" />
            <FeatureText variant="body02">{listing.attributes.carSpaces}</FeatureText>
          </GeneralFeature>
        )}
        <PropertyType variant="body02">
          {listing.attributes.propertyTypes
            .map((propertyType) => getPropertyTypeFromDisplayText(propertyType)?.longDisplayText)
            .join(', ')}
        </PropertyType>
      </Details>
    </StyledStack>
  </PlainLink>
);

export default ElitePlusCarouselListingCard;
